import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-surface flex flex-col">
      <SEO 
        title="Terms & Conditions"
        description="Terms and conditions for using Strider (Strider) - the remote workspace discovery platform."
        canonical="/terms"
      />
      <Header />
      
      <main className="flex-1 pt-24 pb-12 px-4">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Terms & Conditions</h1>
          <p className="text-gray-500 mb-8">Last updated: March 2026</p>

          <div className="prose prose-gray max-w-none space-y-6">
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Acceptance of Terms</h2>
              <p className="text-gray-600">
                By accessing and using Strider ("Strider", "the Service"), you accept and agree to be bound 
                by the terms and provisions of this agreement. If you do not agree to abide by these terms, 
                please do not use this service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Description of Service</h2>
              <p className="text-gray-600">
                Strider provides a platform for discovering workspaces suitable for remote work, 
                including cafes, hotels, libraries, and coworking spaces. We also provide venue owners with 
                tools to manage their listings and connect with potential customers.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">3. User Accounts</h2>
              <p className="text-gray-600">
                Some features of our Service may require you to create an account. You are responsible for 
                maintaining the confidentiality of your account information and for all activities that occur 
                under your account. You agree to notify us immediately of any unauthorized use of your account.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Venue Listings</h2>
              <p className="text-gray-600">
                Venue owners who list their properties on our platform agree to provide accurate and up-to-date 
                information about their venues. We reserve the right to remove any listings that contain false 
                or misleading information, or that violate our community guidelines.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Premium Subscriptions</h2>
              <p className="text-gray-600">
                Premium features are available through paid subscriptions. Subscription fees are billed in 
                advance on a monthly or annual basis. You may cancel your subscription at any time, but refunds 
                are only available within the first 14 days of a new subscription.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">6. User Content</h2>
              <p className="text-gray-600">
                Users may submit reviews, photos, and other content to the Service. By submitting content, you 
                grant us a non-exclusive, worldwide, royalty-free license to use, reproduce, and display that 
                content in connection with the Service. You are solely responsible for the content you submit.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Prohibited Conduct</h2>
              <p className="text-gray-600">You agree not to:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-600">
                <li>Use the Service for any illegal purpose</li>
                <li>Submit false or misleading information</li>
                <li>Harass, abuse, or harm other users</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Use automated means to access the Service without permission</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Data Protection & Privacy (GDPR)</h2>
              <p className="text-gray-600 mb-3">
                We are committed to protecting your personal data in accordance with the UK General Data Protection 
                Regulation (UK GDPR) and the Data Protection Act 2018. This section explains how we collect, use, 
                and protect your information.
              </p>
              
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">8.1 Data We Collect</h3>
              <p className="text-gray-600 mb-2">When you use our Service, we may collect:</p>
              <ul className="list-disc pl-6 space-y-1 text-gray-600">
                <li><strong>Account Information:</strong> Name, email address, profile picture (if using Google Sign-In)</li>
                <li><strong>Contribution Data:</strong> Reviews, ratings, venue submissions, WiFi speed test results</li>
                <li><strong>Technical Data:</strong> Device type, browser type, IP address, location data (with your consent)</li>
                <li><strong>Usage Data:</strong> Pages visited, features used, time spent on the platform</li>
              </ul>
              
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">8.2 How We Use Your Data</h3>
              <p className="text-gray-600 mb-2">We use your personal data to:</p>
              <ul className="list-disc pl-6 space-y-1 text-gray-600">
                <li>Provide and improve our Service</li>
                <li>Process your contributions and award gamification points</li>
                <li>Send you relevant communications (with your consent)</li>
                <li>Ensure the security and integrity of our platform</li>
              </ul>
              
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">8.3 Anonymised Data Sharing</h3>
              <p className="text-gray-600 mb-2">
                <strong>Important:</strong> By creating an account and contributing data (reviews, WiFi speed tests, venue information), 
                you consent to us sharing <strong>anonymised and aggregated data</strong> with third parties for commercial purposes. 
                This includes:
              </p>
              <ul className="list-disc pl-6 space-y-1 text-gray-600">
                <li>Aggregated WiFi speed statistics by location and venue type</li>
                <li>Anonymised visitor patterns and workspace usage trends</li>
                <li>Statistical data about remote work preferences and behaviours</li>
                <li>Anonymised review sentiment and rating distributions</li>
              </ul>
              <p className="text-gray-600 mt-2">
                <strong>This anonymised data cannot be used to identify you personally.</strong> All personal identifiers 
                (name, email, specific location) are removed or aggregated before sharing.
              </p>
              
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">8.4 Your Rights Under GDPR</h3>
              <p className="text-gray-600 mb-2">You have the following rights regarding your personal data:</p>
              <ul className="list-disc pl-6 space-y-1 text-gray-600">
                <li><strong>Right of Access:</strong> Request a copy of your personal data</li>
                <li><strong>Right to Rectification:</strong> Request correction of inaccurate data</li>
                <li><strong>Right to Erasure:</strong> Request deletion of your personal data ("right to be forgotten")</li>
                <li><strong>Right to Restrict Processing:</strong> Request limitation of how we use your data</li>
                <li><strong>Right to Data Portability:</strong> Receive your data in a structured, machine-readable format</li>
                <li><strong>Right to Object:</strong> Object to processing based on legitimate interests</li>
                <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time where processing is based on consent</li>
              </ul>
              <p className="text-gray-600 mt-2">
                To exercise any of these rights, contact us at{" "}
                <a href="mailto:privacy@strider.work" className="text-brand hover:underline">privacy@strider.work</a>.
              </p>
              
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">8.5 Data Retention</h3>
              <p className="text-gray-600">
                We retain your personal data for as long as your account is active or as needed to provide you with our Service. 
                You may request deletion of your account and associated data at any time. Anonymised data may be retained 
                indefinitely for statistical and analytical purposes.
              </p>
              
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">8.6 Legal Basis for Processing</h3>
              <p className="text-gray-600 mb-2">We process your personal data on the following legal bases:</p>
              <ul className="list-disc pl-6 space-y-1 text-gray-600">
                <li><strong>Contract:</strong> Processing necessary to perform our contract with you</li>
                <li><strong>Consent:</strong> Where you have given explicit consent (e.g., marketing communications, data sharing)</li>
                <li><strong>Legitimate Interests:</strong> Processing necessary for our legitimate business interests</li>
                <li><strong>Legal Obligation:</strong> Processing necessary to comply with legal requirements</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Limitation of Liability</h2>
              <p className="text-gray-600">
                Strider is provided "as is" without any warranties. We do not guarantee the accuracy 
                of venue information or the quality of any workspace. We are not liable for any damages arising 
                from your use of the Service or your interactions with venues listed on our platform.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">10. Changes to Terms</h2>
              <p className="text-gray-600">
                We reserve the right to modify these terms at any time. We will notify users of significant 
                changes via email or through the Service. Your continued use of the Service after changes 
                constitutes acceptance of the new terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">11. Contact Us</h2>
              <p className="text-gray-600">
                If you have any questions about these Terms & Conditions, please contact us at{" "}
                <a href="mailto:info@strider.work" className="text-brand hover:underline">info@strider.work</a>.
              </p>
              <p className="text-gray-600 mt-2">
                For data protection and privacy enquiries, contact our Data Protection Officer at{" "}
                <a href="mailto:privacy@strider.work" className="text-brand hover:underline">privacy@strider.work</a>.
              </p>
              <p className="text-gray-500 text-sm mt-4">
                Strider is operated by BOLDHORN PRESS LTD (Company No. 17134876).<br />
                ICO Registration Number: [Your ICO Registration Number]
              </p>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
